// Компонент редактируемой ячейки
import React, { useState, useRef, useEffect } from 'react';
import { Input, InputNumber, Select, DatePicker } from 'antd';
import dayjs from 'dayjs';
import type { ColumnConfig, Row } from './types';

interface EditableCellProps {
  value: any;
  column: ColumnConfig;
  rowData: Row;
  onChange: (value: any) => void;
  onPressEnter?: () => void;
  isChanged?: boolean;
  isNew?: boolean; // Новая строка — все поля редактируемые
}

export const EditableCell: React.FC<EditableCellProps> = ({
  value,
  column,
  rowData,
  onChange,
  onPressEnter,
  isChanged = false,
  isNew = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const inputRef = useRef<any>(null);

  // Кастомный рендер (для type: 'custom')
  if (column.type === 'custom' && column.customRender) {
    return <>{column.customRender(value, rowData, onChange)}</>;
  }

  // Вычисляемые колонки не редактируются (кроме новых строк где они пустые)
  const isComputed = !!column.formula;
  // Новые строки — редактируются все колонки кроме вычисляемых
  const isEditable = isNew ? !isComputed : (column.editable !== false && !isComputed);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const handleSave = () => {
    setEditing(false);
    if (tempValue !== value) {
      onChange(tempValue);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setTempValue(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
      onPressEnter?.();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Стили для ячейки
  const cellStyle: React.CSSProperties = {
    padding: '8px',
    cursor: isEditable ? 'pointer' : 'default',
    backgroundColor: isChanged
      ? '#fffbe6' // Жёлтый фон для изменённых ячеек
      : isComputed
      ? '#f5f5f5' // Серый фон для вычисляемых
      : 'transparent',
    minHeight: '40px',
    display: 'flex',
    alignItems: 'center',
    border: isNew && !value ? '1px dashed #91caff' : undefined, // Пунктир для пустых полей в новой строке
  };

  // Рендер редактора в зависимости от типа
  const renderEditor = () => {
    switch (column.type) {
      case 'number':
        return (
          <InputNumber
            ref={inputRef}
            value={tempValue}
            onChange={(val) => setTempValue(val)}
            onPressEnter={handleSave}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            style={{ width: '100%' }}
            min={column.validation?.min}
            max={column.validation?.max}
          />
        );

      case 'select':
        return (
          <Select
            ref={inputRef}
            value={tempValue}
            onChange={(val) => {
              setTempValue(val);
              onChange(val);
              setEditing(false);
            }}
            onBlur={handleSave}
            options={column.options}
            style={{ width: '100%' }}
            open={editing}
            onDropdownVisibleChange={(open) => !open && setEditing(false)}
          />
        );

      case 'date':
        return (
          <DatePicker
            ref={inputRef}
            value={tempValue ? dayjs(tempValue) : null}
            onChange={(date) => {
              const val = date ? date.format('YYYY-MM-DD') : null;
              setTempValue(val);
              onChange(val);
              setEditing(false);
            }}
            onBlur={handleSave}
            style={{ width: '100%' }}
            open={editing}
            onOpenChange={(open) => !open && setEditing(false)}
          />
        );

      case 'text':
      default:
        return (
          <Input
            ref={inputRef}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onPressEnter={handleSave}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            style={{ width: '100%' }}
            maxLength={column.validation?.max}
          />
        );
    }
  };

  // Рендер значения для отображения
  const renderValue = () => {
    if (value === null || value === undefined) {
      return <span style={{ color: '#bfbfbf' }}>—</span>;
    }

    if (column.type === 'date' && value) {
      return dayjs(value).format('DD.MM.YYYY');
    }

    if (column.type === 'select' && column.options) {
      const option = column.options.find((opt) => opt.value === value);
      return option?.label || value;
    }

    if (column.type === 'number' && typeof value === 'number') {
      return value.toLocaleString('ru-RU');
    }

    return String(value);
  };

  if (!isEditable) {
    // Вычисляемые или не редактируемые колонки
    return <div style={cellStyle}>{renderValue()}</div>;
  }

  return (
    <div
      style={cellStyle}
      onClick={() => !editing && setEditing(true)}
      onDoubleClick={() => setEditing(true)}
    >
      {editing ? renderEditor() : renderValue()}
    </div>
  );
};
